import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import AppShell from "../components/AppShell";
import api, { formatError } from "../lib/api";
import { Save, Copy, ArrowLeft, Trash2, Plus, X, CheckCircle2, Download, Loader2, FileText, Cpu, Share2, FileType2, ClipboardCopy, Check } from "lucide-react";
import ShareDialog from "../components/ShareDialog";
import ChatPanel from "../components/ChatPanel";
import AutoTextarea from "../components/AutoTextarea";
import RichText from "../components/RichText";

const LABELS = ["A", "B", "C", "D", "E", "F"];

export default function RattrapageDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [rat, setRat] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [copied, setCopied] = useState(false);
  const [exportText, setExportText] = useState("");
  const [shareOpen, setShareOpen] = useState(false);
  const [copiedQ, setCopiedQ] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/rattrapages/${id}`);
        setRat(data);
      } catch (e) {
        setErr(formatError(e));
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  if (loading) {
    return (
      <AppShell>
        <div className="px-8 lg:px-12 py-10 text-slate-500 text-sm" data-testid="detail-loading">Chargement…</div>
      </AppShell>
    );
  }
  if (!rat) {
    return (
      <AppShell>
        <div className="px-8 lg:px-12 py-10" data-testid="detail-error">
          <div className="text-sm text-[#b91c1c]">{err || "Rattrapage introuvable"}</div>
          <Link to="/dashboard" className="text-sm text-[#2563eb] mt-3 inline-block">Retour</Link>
        </div>
      </AppShell>
    );
  }

  const update = (patch) => setRat((r) => ({ ...r, ...patch }));

  const updateQuestion = (idx, patch) => {
    setRat((r) => {
      const qs = [...r.questions];
      qs[idx] = { ...qs[idx], ...patch };
      return { ...r, questions: qs };
    });
  };

  const updateOption = (qi, oi, text) => {
    setRat((r) => {
      const qs = [...r.questions];
      const opts = [...qs[qi].options];
      opts[oi] = { ...opts[oi], text };
      qs[qi] = { ...qs[qi], options: opts };
      return { ...r, questions: qs };
    });
  };

  const toggleCorrect = (qi, label) => {
    setRat((r) => {
      const qs = [...r.questions];
      const cur = new Set(qs[qi].correct_labels || []);
      if (cur.has(label)) cur.delete(label);
      else cur.add(label);
      qs[qi] = { ...qs[qi], correct_labels: Array.from(cur).sort() };
      return { ...r, questions: qs };
    });
  };

  const addOption = (qi) => {
    setRat((r) => {
      const qs = [...r.questions];
      const opts = [...qs[qi].options];
      const nextLabel = LABELS[opts.length] || `OPT${opts.length + 1}`;
      opts.push({ label: nextLabel, text: "" });
      qs[qi] = { ...qs[qi], options: opts };
      return { ...r, questions: qs };
    });
  };

  const removeOption = (qi, oi) => {
    setRat((r) => {
      const qs = [...r.questions];
      const removedLabel = qs[qi].options[oi].label;
      const opts = qs[qi].options.filter((_, i) => i !== oi).map((o, i) => ({ ...o, label: LABELS[i] || o.label }));
      const correct = (qs[qi].correct_labels || []).filter((l) => l !== removedLabel);
      qs[qi] = { ...qs[qi], options: opts, correct_labels: correct };
      return { ...r, questions: qs };
    });
  };

  const save = async () => {
    setSaving(true);
    setErr("");
    try {
      const { data } = await api.put(`/rattrapages/${id}`, {
        title: rat.title,
        module: rat.module,
        duration_minutes: rat.duration_minutes,
        questions: rat.questions,
        status: rat.status,
      });
      setRat(data);
    } catch (e) {
      setErr(formatError(e));
    } finally {
      setSaving(false);
    }
  };

  const loadExport = async () => {
    try {
      const { data } = await api.get(`/rattrapages/${id}/export`);
      setExportText(data.text);
      return data.text;
    } catch (e) {
      setErr(formatError(e));
      return "";
    }
  };

  const copyForForms = async () => {
    let text = exportText;
    if (!text) text = await loadExport();
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch {
      setErr("Impossible d'accéder au presse-papiers");
    }
  };

  const downloadText = async () => {
    let text = exportText;
    if (!text) text = await loadExport();
    if (!text) return;
    const blob = new Blob([text], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${rat.title.replace(/[^a-zA-Z0-9-_]+/g, "_")}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const onDelete = async () => {
    if (!window.confirm("Supprimer définitivement ce rattrapage ?")) return;
    try {
      await api.delete(`/rattrapages/${id}`);
      navigate("/dashboard");
    } catch (e) {
      setErr(formatError(e));
    }
  };

  const downloadDocx = async () => {
    try {
      const res = await api.get(`/rattrapages/${id}/export.docx`, { responseType: "blob" });
      const safe = (rat.title || "rattrapage").replace(/[^a-zA-Z0-9-_]+/g, "_");
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${safe}.docx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setErr(formatError(e));
    }
  };

  const copyQuestion = async (qIndex) => {
    try {
      const { data } = await api.get(`/rattrapages/${id}/question/${qIndex + 1}/text`);
      // Write rich HTML + plain fallback to clipboard so Forms preserves formatting
      if (window.ClipboardItem && navigator.clipboard?.write) {
        const item = new ClipboardItem({
          "text/html": new Blob([data.html || ""], { type: "text/html" }),
          "text/plain": new Blob([data.text || ""], { type: "text/plain" }),
        });
        await navigator.clipboard.write([item]);
      } else {
        await navigator.clipboard.writeText(data.text || "");
      }
      setCopiedQ(qIndex);
      setTimeout(() => setCopiedQ(null), 2000);
    } catch (e) {
      setErr(formatError(e));
    }
  };

  const modelLabel = rat.ai_model === "gpt-5.2" ? "GPT-5.2" : "Claude Sonnet 4.5";

  return (
    <AppShell>
      <div className="px-8 lg:px-12 py-10 max-w-[1200px]" data-testid="detail-page">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <Link to="/dashboard" className="text-sm text-slate-600 hover:text-slate-900 inline-flex items-center gap-1" data-testid="back-link">
            <ArrowLeft size={14} /> Retour au tableau de bord
          </Link>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShareOpen(true)}
              data-testid="open-share-btn"
              className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-semibold bg-white border border-slate-300 rounded-sm hover:bg-slate-50"
            >
              <Share2 size={14} /> Partager pour relecture
            </button>
            <button
              onClick={save}
              disabled={saving}
              data-testid="save-btn"
              className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-semibold border border-slate-300 rounded-sm hover:bg-slate-50 disabled:opacity-50"
            >
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
              Enregistrer
            </button>
            <button
              onClick={onDelete}
              data-testid="delete-btn"
              className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-semibold text-[#b91c1c] border border-red-200 rounded-sm hover:bg-red-50"
            >
              <Trash2 size={14} /> Supprimer
            </button>
          </div>
        </div>

        <div className="mt-6 bg-white border border-slate-200 rounded-md p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="label-xs block mb-2">Titre du formulaire</label>
              <input
                value={rat.title}
                onChange={(e) => update({ title: e.target.value })}
                data-testid="title-input"
                className="w-full px-3 py-2 text-sm border border-slate-300 rounded-sm focus:outline-none focus:border-slate-900 focus:ring-1 focus:ring-slate-900"
              />
            </div>
            <div>
              <label className="label-xs block mb-2">Module</label>
              <input
                value={rat.module}
                onChange={(e) => update({ module: e.target.value })}
                data-testid="module-input"
                className="w-full px-3 py-2 text-sm border border-slate-300 rounded-sm focus:outline-none focus:border-slate-900 focus:ring-1 focus:ring-slate-900"
              />
            </div>
            <div>
              <label className="label-xs block mb-2">Durée (minutes)</label>
              <input
                type="number"
                min={1}
                value={rat.duration_minutes}
                onChange={(e) => update({ duration_minutes: parseInt(e.target.value || "60", 10) })}
                data-testid="duration-input"
                className="w-full px-3 py-2 text-sm border border-slate-300 rounded-sm focus:outline-none focus:border-slate-900 focus:ring-1 focus:ring-slate-900"
              />
            </div>
            <div className="flex flex-wrap items-end gap-2">
              <span className="inline-flex items-center gap-1.5 text-xs px-2 py-1 bg-blue-50 text-[#2563eb] rounded-sm font-semibold">
                <Cpu size={11} /> {modelLabel}
              </span>
              <span className="inline-flex items-center gap-1.5 text-xs px-2 py-1 bg-slate-100 text-slate-700 rounded-sm font-semibold">
                <FileText size={11} /> {rat.questions?.length || 0} questions
              </span>
            </div>
          </div>
        </div>

        <div className="mt-6 space-y-4">
          {(rat.questions || []).map((q, qi) => (
            <div key={qi} className="bg-white border border-slate-200 rounded-md p-6 reveal" data-testid={`question-${qi}`}>
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <div className="w-8 h-8 rounded-sm bg-slate-900 text-white text-sm font-bold flex items-center justify-center shrink-0">
                    {qi + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="label-xs">Question {qi + 1}</div>
                    <input
                      type="text"
                      value={q.title || ""}
                      onChange={(e) => updateQuestion(qi, { title: e.target.value })}
                      placeholder="Titre court de la question (ex. « Tri par insertion »)"
                      data-testid={`question-${qi}-title`}
                      className="mt-1 w-full bg-transparent border-0 border-b border-transparent hover:border-slate-200 focus:border-slate-900 focus:outline-none focus:ring-0 font-display text-lg font-semibold text-slate-900 px-0 py-0.5"
                    />
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => copyQuestion(qi)}
                  data-testid={`copy-question-${qi}`}
                  className="inline-flex items-center gap-1.5 text-xs text-slate-600 hover:text-slate-900 border border-slate-200 rounded-sm px-2 py-1 bg-white shrink-0"
                  title="Copier cette question seule (titre + énoncé, avec mise en forme) pour Microsoft Forms"
                >
                  {copiedQ === qi ? <><Check size={12} className="text-[#15803d]" /> Copié</> : <><ClipboardCopy size={12} /> Copier pour Forms</>}
                </button>
              </div>

              <div className="mt-4">
                <div className="label-xs mb-1.5">Énoncé — formatage : <code className="px-1 bg-slate-100 rounded">`code`</code> · <code className="px-1 bg-slate-100 rounded">```bloc```</code></div>
                <AutoTextarea
                  value={q.statement}
                  onChange={(e) => updateQuestion(qi, { statement: e.target.value })}
                  data-testid={`question-${qi}-statement`}
                  minRows={4}
                  className="w-full px-3 py-2 text-sm border border-slate-300 rounded-sm focus:outline-none focus:border-slate-900 focus:ring-1 focus:ring-slate-900 leading-relaxed font-mono"
                />
                {q.statement && (q.statement.includes("```") || q.statement.includes("`")) && (
                  <div className="mt-2 px-3 py-3 bg-slate-50 border border-slate-200 rounded-sm" data-testid={`question-${qi}-preview`}>
                    <div className="label-xs mb-2">Aperçu rendu</div>
                    <RichText className="text-sm text-slate-800">{q.statement}</RichText>
                  </div>
                )}
              </div>

              <div className="mt-4 space-y-2">
                {(q.options || []).map((opt, oi) => {
                  const isCorrect = (q.correct_labels || []).includes(opt.label);
                  return (
                    <div
                      key={oi}
                      className={`flex items-start gap-2 border rounded-sm px-3 py-2 ${
                        isCorrect ? "border-green-300 bg-green-50/50" : "border-slate-200 bg-white"
                      }`}
                      data-testid={`question-${qi}-option-${oi}`}
                    >
                      <button
                        type="button"
                        onClick={() => toggleCorrect(qi, opt.label)}
                        className={`mt-1 w-5 h-5 rounded-sm border flex items-center justify-center shrink-0 ${
                          isCorrect ? "bg-[#15803d] border-[#15803d] text-white" : "bg-white border-slate-300"
                        }`}
                        title="Marquer comme correcte"
                        data-testid={`toggle-correct-${qi}-${opt.label}`}
                      >
                        {isCorrect && <CheckCircle2 size={12} />}
                      </button>
                      <div className="font-semibold text-sm w-6 shrink-0 text-slate-700">{opt.label}.</div>
                      <AutoTextarea
                        value={opt.text}
                        minRows={1}
                        onChange={(e) => updateOption(qi, oi, e.target.value)}
                        data-testid={`option-text-${qi}-${oi}`}
                        className="flex-1 px-2 py-1 text-sm bg-transparent border-0 focus:outline-none focus:ring-0 leading-relaxed"
                      />
                      <button
                        type="button"
                        onClick={() => removeOption(qi, oi)}
                        className="text-slate-400 hover:text-[#b91c1c] mt-1"
                        title="Retirer cette proposition"
                        data-testid={`remove-option-${qi}-${oi}`}
                      >
                        <X size={14} />
                      </button>
                    </div>
                  );
                })}
                {q.options.length < 6 && (
                  <button
                    type="button"
                    onClick={() => addOption(qi)}
                    className="inline-flex items-center gap-1 text-xs text-slate-600 hover:text-slate-900 mt-1"
                    data-testid={`add-option-${qi}`}
                  >
                    <Plus size={12} /> Ajouter une proposition
                  </button>
                )}
              </div>

              <div className="mt-5">
                <div className="label-xs mb-1.5">Explication (réservée à l'équipe pédagogique — non affichée au répondant)</div>
                <AutoTextarea
                  value={q.explanation || ""}
                  onChange={(e) => updateQuestion(qi, { explanation: e.target.value })}
                  minRows={2}
                  data-testid={`question-${qi}-explanation`}
                  className="w-full px-3 py-2 text-xs text-slate-700 border border-slate-200 rounded-sm bg-slate-50 focus:outline-none focus:border-slate-400 leading-relaxed"
                />
              </div>
            </div>
          ))}
        </div>

        {err && (
          <div className="mt-4 text-sm text-[#b91c1c] bg-[#fef2f2] border border-red-200 px-3 py-2 rounded-sm" data-testid="detail-error">
            {err}
          </div>
        )}

        {/* Export panel */}
        <div className="mt-8 bg-slate-900 text-white rounded-md p-6" data-testid="export-panel">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <div className="label-xs text-white/60">Export pour Microsoft Forms</div>
              <h3 className="font-display text-xl font-semibold mt-1">Deux façons de l'utiliser dans Forms</h3>
              <ul className="text-sm text-slate-300 mt-2 max-w-xl space-y-1.5 list-disc list-inside">
                <li><strong className="text-white">Word (.docx)</strong> — glissez-le dans la fonction « Suggérer des questions » de Forms (Copilot) pour un import quasi-automatique.</li>
                <li><strong className="text-white">Copier question par question</strong> — bouton « Copier pour Forms » sur chaque question ci-dessus, pour un collage rapide dans Forms classique.</li>
                <li><strong className="text-white">Tout copier</strong> — texte intégral, utile pour archivage ou autre outil.</li>
              </ul>
              <div className="text-xs text-slate-400 mt-3">
                ⚠️ Pensez à activer la durée <strong>60 minutes</strong> et à désactiver l'affichage des résultats côté Forms.
              </div>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <button
                onClick={downloadDocx}
                data-testid="download-docx-btn"
                className="inline-flex items-center gap-1.5 bg-[#2563eb] text-white px-3 py-2 text-sm font-semibold rounded-sm hover:bg-blue-700"
              >
                <FileType2 size={14} /> Télécharger .docx
              </button>
              <button
                onClick={copyForForms}
                data-testid="copy-export-btn"
                className="inline-flex items-center gap-1.5 bg-white text-slate-900 px-3 py-2 text-sm font-semibold rounded-sm hover:bg-slate-100"
              >
                <Copy size={14} /> {copied ? "Copié !" : "Tout copier"}
              </button>
              <button
                onClick={downloadText}
                data-testid="download-export-btn"
                className="inline-flex items-center gap-1.5 border border-white/20 text-white px-3 py-2 text-sm font-semibold rounded-sm hover:bg-white/10"
              >
                <Download size={14} /> .txt
              </button>
            </div>
          </div>

          {exportText && (
            <pre className="mt-5 max-h-80 overflow-auto bg-black/30 border border-white/10 rounded-sm p-4 text-xs leading-relaxed whitespace-pre-wrap" data-testid="export-preview">
              {exportText}
            </pre>
          )}
        </div>

        {/* Chat panel — refine with AI */}
        <div className="mt-8" data-testid="chat-section">
          <ChatPanel
            rattrapageId={rat.id}
            onUpdated={(updated) => setRat((prev) => ({ ...prev, ...updated }))}
          />
        </div>
      </div>

      <ShareDialog
        rattrapageId={rat.id}
        rattrapageTitle={rat.title}
        open={shareOpen}
        onClose={() => setShareOpen(false)}
      />
    </AppShell>
  );
}
